import React, { useState, useEffect } from "react";
import { useTheme } from "../../../ThemeContext";

const SectionSelector = ({
  setSelectedSections,
  uniqueSections,
  setStartDate,
  startDate,
  setEndDate,
  endDate,
  setDay,
  day,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const sections = uniqueSections;

  // Local state for selected sections (all sections are selected by default)
  const [selectedSections, setSelectedSectionsState] = useState(""); 
  useEffect(()=>{
    setSelectedSectionsState(sections)
  },[sections])

  // Calculate the number of days in the month based on the StartDate
  const getDaysInMonth = (date) => {
    const currentDate = new Date(date);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // Get the month index (0-11)
    
    // Get the number of days in the given month
    return new Date(year, month + 1, 0).getDate(); // The last day of the month
  };

  // Generate the list of days based on the StartDate
  const generateDays = (startDate) => {
    const daysInMonth = getDaysInMonth(startDate);
    const daysArray = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }
    return daysArray;
  };

  // Get the list of days in the month from the StartDate
  const daysInMonth = generateDays(startDate);

  // Handle toggling selection for a section
  const toggleSection = (section) => {
    setSelectedSectionsState((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section) // Deselect if already selected
        : [...prev, section] // Add to selection if not already selected
    );
  };

  // Select all sections
  const handleSelectAll = () => {
    setSelectedSectionsState(sections); // Select all sections
  };

  // Deselect all sections
  const handleDeselectAll = () => {
    setSelectedSectionsState([]); // Clear selection
  };

  // Notify parent component of selection changes
  useEffect(() => {
    if (setSelectedSections) {
      setSelectedSections(selectedSections); // Pass selected sections to the parent
    }
  }, [selectedSections, setSelectedSections]);

  // Handle day change
  const handleDayChange = (e) => {
    const selectedDay = e.target.value;
    setDay(selectedDay); // Pass the selected day to the parent
  };

  // Apply theme-specific classes
  const inputClasses = isDarkMode
    ? "w-full px-3 py-2 border rounded bg-gray-700 text-white"
    : "w-full px-3 py-2 border rounded bg-white text-black";

  const buttonClasses = isDarkMode
    ? "bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 transition"
    : "bg-blue-700 text-white py-1 px-3 rounded hover:bg-blue-800 transition";

  const selectClasses = isDarkMode
    ? "w-full px-3 py-2 border rounded bg-gray-700 text-white"
    : "w-full px-3 py-2 border rounded bg-white text-black";

  // Set default end date to today if not provided
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]; // Get the current date in YYYY-MM-DD format
    if (!endDate) {
      setEndDate(today); // Default end date
    }
  }, [endDate, setEndDate]);

  return (
    <div className="p-3">
      <p className="my-3 text-md text-center">Sections</p>

      {/* Buttons to select/deselect all sections */}
      <div className="flex justify-between mb-2">
        <button
          onClick={handleSelectAll}
          className={buttonClasses}
        >
          Select All
        </button>
        <button
          onClick={handleDeselectAll}
          className={`${buttonClasses} bg-red-500 hover:bg-red-600`}
        >
          Deselect All
        </button>
      </div>

      {/* Display section options */}
      <div className="grid grid-cols-2 gap-2 w-56">
        {sections.map((section, index) => (
          <label
            key={index}
            className={`flex items-center space-x-2 py-2 px-2 rounded hover:bg-blue-600 transition cursor-pointer ${selectedSections.includes(section) ? "bg-blue-700" : "bg-blue-500"}`}
          >
            <input
              type="checkbox"
              checked={selectedSections.includes(section)}
              onChange={() => toggleSection(section)}
              className="form-checkbox"
            />
            <span>{section}</span>
          </label>
        ))}
      </div>

      {/* Date Range and Day Filter */}
      <div className="mt-4">
        <label htmlFor="start-date" className="block">Start Date</label>
        <input
          type="date"
          id="start-date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className={inputClasses}
        />

        <label htmlFor="end-date" className="block mt-2">End Date</label>
        <input
          type="date"
          id="end-date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className={inputClasses}
        />

        {/* Day of the Month Filter */}
        <label htmlFor="day" className="block mt-2">Day of the Month</label>
        <select
          id="day"
          onChange={handleDayChange}
          value={day || ""}
          className={selectClasses}
        >
          <option value="">Select Day</option>
          {daysInMonth.map((dayOption) => (
            <option key={dayOption} value={dayOption}>
              {dayOption}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default SectionSelector;
